import {
  Anchor, Badge, Button, Card, Drawer, FileInput, TagsInput, Group, Modal, Pagination, Paper, SegmentedControl, Select, SimpleGrid, Stack, Table,
  Tabs, Text, TextInput, Textarea, Timeline, Title, Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle, IconArchive, IconCheck, IconDownload, IconSearch, IconUpload } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { downloadFile, http } from "../../api/http";
import { useCan } from "../../auth/user";
import { DataTable } from "../../components/DataTable";
import { Empty, fmtDate, fmtDateTime, fmtNum, KpiGrid, PageHeader, StatusBadge } from "../../components/ui";
import { useOpstina } from "../../context/opstina";
import { useT } from "../../i18n/script";

interface DocType { code: string; name: string; retention_years: number | null; legal_basis: string | null; classification?: string; archival?: boolean }
interface Doc {
  id: number; reg_no: string; title: string; doc_type: string; doc_type_name: string; doc_date: string; status: string;
  current_version: number; retain_until: string | null; tags: string[]; created_by: string; created_at: string; opstina?: string;
  producer?: string; bpg?: string; latest?: { filename: string; mime: string; size: number } | null; snippet?: string | null;
  links?: { type: string; id: number }[] | null;
}

const fmtSize = (b?: number) => (b == null ? "—" : b > 1e6 ? `${fmtNum(b / 1e6, 1)} MB` : `${fmtNum(b / 1e3, 0)} kB`);

/** Katastarske opštine (izbor na dokumentu i u pretrazi). */
export function useKoList() {
  return useQuery({ queryKey: ["ko-list"], staleTime: 600_000,
    queryFn: () => http.get<{ id: number; name: string; opstina: string | null }[]>("/api/v1/cases/lookup/ko") });
}

export const EVIDENCIJE = [
  { value: "NN", label: "NN", title: "Katastar nepokretnosti" },
  { value: "KZ", label: "KZ", title: "Katastar zemljišta" },
  { value: "KN", label: "KN", title: "Novi katastar" },
];
export const evidLabel = (v?: string | null) => EVIDENCIJE.find((e) => e.value === v)?.title ?? "";
export interface ParcelCheck { broj: string; found: boolean; parcel_id: number | null; povrsina_m2: number | null; kultura: string | null; poruka: string | null }
/** Vrijednost KO: id iz evidencije ili „free:Naziv“ za KO koja nije u evidenciji. */
export const koValue = (ko_id?: number | null, ko_name?: string | null) => (ko_id ? String(ko_id) : ko_name ? `free:${ko_name}` : null);

/**
 * KO + jedna ili više katastarskih čestica (KČ) na dokumentu, uz vrstu evidencije (NN / KZ / KN).
 * KO i KČ se biraju iz evidencije (veza sa katastrom i kartom) ili upisuju slobodno kada ih u evidenciji nema;
 * za upisani niz sistem odmah pokazuje koje čestice prepoznaje, a koje ne.
 */
export function KoParcelsFields({ ko, setKo, parcels, setParcels, evid, setEvid }: { ko: string | null; setKo: (v: string | null) => void;
  parcels: string[]; setParcels: (v: string[]) => void; evid: string | null; setEvid: (v: string | null) => void }) {
  const { t } = useT();
  const kos = useKoList();
  const [koSearch, setKoSearch] = useState("");
  const [kcSearch, setKcSearch] = useState("");
  const koId = ko && !ko.startsWith("free:") ? Number(ko) : null;
  const koData = useMemo(() => {
    const base = (kos.data ?? []).map((k) => ({ value: String(k.id), label: `${k.name}${k.opstina ? ` (${k.opstina})` : ""}` }));
    const s = koSearch.trim();
    const freeCur = ko?.startsWith("free:") ? [{ value: ko, label: `${ko.slice(5)} — ${t("nije u evidenciji")}` }] : [];
    const exact = base.some((o) => o.label.toLowerCase().startsWith(s.toLowerCase()));
    const freeNew = s.length >= 2 && !exact && `free:${s}` !== ko ? [{ value: `free:${s}`, label: `${s} — ${t("upiši kao KO van evidencije")}` }] : [];
    return [...freeCur, ...base, ...freeNew];
  }, [kos.data, koSearch, ko, t]);
  const sugg = useQuery({ queryKey: ["kc-sugg", koId, kcSearch], enabled: !!koId && kcSearch.trim().length >= 1, staleTime: 60_000,
    queryFn: () => http.get<{ label: string; land_use: string | null }[]>("/api/v1/cadastre/parcels/search", { ko_id: koId, q: kcSearch.trim(), limit: 25 }) });
  const check = useQuery({ queryKey: ["kc-check", ko, parcels.join(",")], enabled: parcels.length > 0, staleTime: 60_000,
    queryFn: () => http.get<ParcelCheck[]>("/api/v1/dms/parcel-check", { ko_id: koId, parcels: parcels.join(",") }) });
  const found = (check.data ?? []).filter((c) => c.found).length;
  return (
    <Stack gap={6}>
      <Group gap="xs" align="flex-end" wrap="wrap">
        <div>
          <Text size="sm" fw={500} mb={4}>{t("Evidencija")}</Text>
          <SegmentedControl size="xs" value={evid ?? ""} onChange={(v) => setEvid(v || null)}
            data={EVIDENCIJE.map((e) => ({ value: e.value, label: <Tooltip label={t(e.title)} withArrow><span>{e.label}</span></Tooltip> }))} />
        </div>
        <Text size="xs" c="dimmed" mb={6}>{evid ? t(evidLabel(evid)) : t("Izaberite vrstu evidencije")}</Text>
      </Group>
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Select label={t("Katastarska opština (KO)")} searchable clearable value={ko} limit={60}
          onChange={(v) => { setKo(v); setKoSearch(""); }} searchValue={koSearch} onSearchChange={setKoSearch}
          data={koData} nothingFoundMessage={t("Upišite najmanje dva slova")}
          description={t("Iz evidencije ili slobodan upis ako je nema")} />
        <TagsInput label={t("Katastarske čestice (KČ)")} description={t("Birajte iz liste ili upišite niz: 1234/1, 1235; 1236/2")}
          placeholder="1234/1" value={parcels} disabled={!ko} clearable splitChars={[",", " ", ";"]}
          onChange={(v) => setParcels(Array.from(new Set(v.flatMap((x) => x.split(/[,;\s]+/)).map((x) => x.replace(/\s*\/\s*/g, "/").trim()).filter(Boolean))))}
          searchValue={kcSearch} onSearchChange={setKcSearch}
          data={Array.from(new Set((sugg.data ?? []).map((p) => p.label))).filter((l) => !parcels.includes(l))} maxDropdownHeight={220} />
      </SimpleGrid>
      {parcels.length > 0 && (
        <Paper withBorder radius="md" p="xs">
          <Group gap={6} mb={4}>
            <Text size="xs" fw={600}>{t("Provjera u evidenciji")}:</Text>
            <Text size="xs" c="dimmed">{check.isFetching ? t("provjera…") : `${found} ${t("od")} ${parcels.length} ${t("prepoznato")}`}</Text>
          </Group>
          <Group gap={6}>
            {(check.data ?? []).map((c) => (
              <Tooltip key={c.broj} withArrow multiline w={260} label={c.found
                ? `${t("Postoji u evidenciji")}${c.povrsina_m2 ? ` · ${fmtNum(c.povrsina_m2, 0)} m²` : ""}${c.kultura ? ` · ${c.kultura}` : ""} — ${t("veza sa katastrom i kartom")}`
                : t(c.poruka ?? "Nije u evidenciji")}>
                <Badge variant="light" color={c.found ? "green" : "orange"} leftSection={c.found ? <IconCheck size={11} /> : <IconAlertTriangle size={11} />}>
                  {c.broj}</Badge>
              </Tooltip>
            ))}
          </Group>
        </Paper>
      )}
    </Stack>
  );
}

/** Prikaz KO / KČ na dokumentu (prepoznate čestice zeleno, slobodan unos narandžasto). */
export function CadastralBadges({ doc }: { doc: { ko_name?: string | null; parcel_numbers?: string[] | null; parcel_unknown?: string[] | null; evidencija?: string | null; ko_id?: number | null } }) {
  const { t } = useT();
  const unk = new Set(doc.parcel_unknown ?? []);
  return (
    <Group gap={4} mt={4}>
      {doc.evidencija && <Tooltip label={t(evidLabel(doc.evidencija))}><Badge variant="filled" color="soil">{doc.evidencija}</Badge></Tooltip>}
      <Badge variant="light" color={doc.ko_id ? "soil" : "orange"}>KO {doc.ko_name}</Badge>
      {(doc.parcel_numbers ?? []).map((n) => (
        <Tooltip key={n} label={unk.has(n) ? t("Nije u evidenciji — slobodan unos") : t("Prepoznata u evidenciji")}>
          <Badge variant="outline" color={unk.has(n) ? "orange" : "soil"}>KČ {n}</Badge>
        </Tooltip>
      ))}
    </Group>
  );
}

export function useDocTypes() {
  return useQuery({ queryKey: ["doc-types"], staleTime: 300_000, queryFn: () => http.get<DocType[]>("/api/v1/dms/doc-types") });
}

function UploadForm({ holdingId, entity, onDone }: { holdingId?: number; entity?: [string, number]; onDone: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const types = useDocTypes();
  const { units, opstina } = useOpstina();
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [type, setType] = useState<string | null>("ostalo");
  const [date, setDate] = useState("");
  const [desc, setDesc] = useState("");
  const [tags, setTags] = useState("");
  const [ko, setKo] = useState<string | null>(null);
  const [kc, setKc] = useState<string[]>([]);
  const [evid, setEvid] = useState<string | null>("KZ");
  const [unit, setUnit] = useState<string | null>(units.find((u) => u.code === opstina)?.id?.toString() ?? null);
  const up = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append("file", file!);
      fd.append("title", title || file!.name);
      fd.append("doc_type", type ?? "ostalo");
      if (date) fd.append("doc_date", date);
      if (desc) fd.append("description", desc);
      if (tags) fd.append("tags", tags);
      if (holdingId) fd.append("holding_id", String(holdingId));
      else if (unit) fd.append("admin_unit_id", unit);
      if (entity) fd.append("links", JSON.stringify([entity]));
      if (ko?.startsWith("free:")) fd.append("ko_name", ko.slice(5));
      else if (ko) fd.append("ko_id", ko);
      if (ko && kc.length) fd.append("parcels", kc.join(","));
      if (ko && evid) fd.append("evidencija", evid);
      return http.form<{ reg_no: string }>("/api/v1/dms/documents", fd);
    },
    onSuccess: (r) => { notifications.show({ color: "green", message: `${t("Zaveden dokument")} ${r.reg_no}` }); qc.invalidateQueries({ queryKey: ["dms"] }); onDone(); },
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }),
  });
  const dt = types.data?.find((x) => x.code === type);
  return (
    <Stack>
      <FileInput label={t("Fajl")} required value={file} onChange={setFile} placeholder={t("Izaberite fajl (PDF, slika, Office...)")} leftSection={<IconUpload size={16} />} />
      <TextInput label={t("Naslov")} value={title} onChange={(e) => setTitle(e.currentTarget.value)} placeholder={file?.name} />
      <SimpleGrid cols={2}>
        <Select label={t("Tip dokumenta")} required data={(types.data ?? []).map((x) => ({ value: x.code, label: x.name }))} value={type} onChange={setType} searchable />
        <TextInput label={t("Datum dokumenta")} type="date" value={date} onChange={(e) => setDate(e.currentTarget.value)} />
        {!holdingId && <Select label={t("Opština")} data={units.map((u) => ({ value: String(u.id), label: u.name }))} value={unit} onChange={setUnit} clearable searchable />}
        <TextInput label={t("Oznake (zarezom)")} value={tags} onChange={(e) => setTags(e.currentTarget.value)} />
      </SimpleGrid>
      <KoParcelsFields ko={ko} setKo={setKo} parcels={kc} setParcels={setKc} evid={evid} setEvid={setEvid} />
      <Textarea label={t("Opis")} value={desc} onChange={(e) => setDesc(e.currentTarget.value)} autosize minRows={2} />
      {dt && <Text size="xs" c="dimmed">{t("Rok čuvanja")}: {dt.retention_years ? `${dt.retention_years} ${t("god.")}` : t("trajno / nije određen")}
        {dt.legal_basis ? ` · ${dt.legal_basis}` : ""}</Text>}
      <Group justify="flex-end"><Button onClick={() => up.mutate()} disabled={!file} loading={up.isPending}>{t("Zavedi dokument")}</Button></Group>
    </Stack>
  );
}

function DocDetail({ id, onClose }: { id: number; onClose: () => void }) {
  const { t } = useT();
  const can = useCan();
  const qc = useQueryClient();
  const [nv, setNv] = useState<File | null>(null);
  const d = useQuery({ queryKey: ["dms", "doc", id], queryFn: () => http.get<Doc & {
    description?: string; versions: { version: number; filename: string; mime_type: string; size_bytes: number; sha256: string; created_by: string; created_at: string; comment?: string }[];
    history: { action: string; details: Record<string, unknown>; username: string; ts: string }[]; legal_basis?: string; archive_book_no?: number;
  }>(`/api/v1/dms/documents/${id}`) });
  const addVersion = useMutation({
    mutationFn: () => { const fd = new FormData(); fd.append("file", nv!); return http.form(`/api/v1/dms/documents/${id}/versions`, fd); },
    onSuccess: () => { setNv(null); qc.invalidateQueries({ queryKey: ["dms"] }); },
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }),
  });
  const archive = useMutation({
    mutationFn: () => http.post(`/api/v1/dms/documents/${id}/archive`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dms"] }),
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }),
  });
  const x = d.data;
  return (
    <Drawer opened onClose={onClose} position="right" size="xl" title={x ? <Text fw={650}>{x.reg_no}</Text> : ""}>
      {x && (
        <Stack>
          <Title order={4}>{x.title}</Title>
          <Group gap="xs"><Badge variant="light">{x.doc_type_name}</Badge><StatusBadge status={x.status} />
            {x.archive_book_no && <Badge color="grape" variant="outline">{t("Arhivska knjiga")} br. {x.archive_book_no}</Badge>}</Group>
          <SimpleGrid cols={2} spacing={4}>
            <Text size="sm"><b>{t("Datum")}:</b> {fmtDate(x.doc_date)}</Text>
            <Text size="sm"><b>{t("Čuva se do")}:</b> {fmtDate(x.retain_until)}</Text>
            <Text size="sm"><b>{t("Opština")}:</b> {x.opstina ?? "—"}</Text>
            <Text size="sm"><b>{t("Proizvođač")}:</b> {x.producer ? `${x.producer} (${x.bpg})` : "—"}</Text>
            <Text size="sm"><b>{t("Zaveo")}:</b> <span data-no-translit>{x.created_by}</span> · {fmtDateTime(x.created_at)}</Text>
            <Text size="sm"><b>{t("Osnov")}:</b> {x.legal_basis ?? "—"}</Text>
          </SimpleGrid>
          {x.description && <Text size="sm">{x.description}</Text>}
          <CadastralEdit doc={x} canEdit={can("dms:write") && x.status !== "arhiviran"} onSaved={() => qc.invalidateQueries({ queryKey: ["dms"] })} />
          <Title order={6}>{t("Verzije")}</Title>
          <Table fz="sm"><Table.Tbody>
            {x.versions.map((v) => (
              <Table.Tr key={v.version}>
                <Table.Td>{`v${v.version}`}</Table.Td><Table.Td>{v.filename}</Table.Td><Table.Td>{fmtSize(v.size_bytes)}</Table.Td>
                <Table.Td><span data-no-translit>{v.created_by}</span><br /><Text size="xs" c="dimmed">{fmtDateTime(v.created_at)}</Text></Table.Td>
                <Table.Td><Text size="xs" ff="monospace" title={v.sha256}>{v.sha256.slice(0, 12)}…</Text></Table.Td>
                <Table.Td><Button size="xs" variant="subtle" leftSection={<IconDownload size={14} />}
                  onClick={() => downloadFile(`/api/v1/dms/documents/${id}/versions/${v.version}/file`, {}, v.mime_type === "application/pdf" || v.mime_type.startsWith("image/"))
                    .catch((e) => notifications.show({ color: "red", message: e.message }))}>{t("Otvori")}</Button></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody></Table>
          {can("dms:write") && x.status !== "arhiviran" && (
            <Group align="flex-end">
              <FileInput label={t("Nova verzija")} value={nv} onChange={setNv} style={{ flex: 1 }} />
              <Button onClick={() => addVersion.mutate()} disabled={!nv} loading={addVersion.isPending}>{t("Dodaj verziju")}</Button>
            </Group>
          )}
          {can("dms:admin") && x.status !== "arhiviran" && (
            <Button variant="light" color="grape" leftSection={<IconArchive size={16} />} onClick={() => archive.mutate()}>{t("Arhiviraj (upis u arhivsku knjigu)")}</Button>
          )}
          <Title order={6}>{t("Istorijat")}</Title>
          <Timeline bulletSize={14} lineWidth={2}>
            {x.history.map((h, i) => (
              <Timeline.Item key={i} title={<Text size="sm" fw={600}>{h.action}</Text>}>
                <Text size="xs" c="dimmed">{h.username} · {fmtDateTime(h.ts)}</Text>
                <Text size="xs">{Object.entries(h.details ?? {}).map(([k, v]) => `${k}: ${String(v)}`).join(" · ")}</Text>
              </Timeline.Item>
            ))}
          </Timeline>
        </Stack>
      )}
    </Drawer>
  );
}

function CadastralEdit({ doc, canEdit, onSaved }: { doc: { id: number; ko_id?: number | null; ko_name?: string | null; parcel_numbers?: string[];
  parcel_unknown?: string[] | null; evidencija?: string | null };
  canEdit: boolean; onSaved: () => void }) {
  const { t } = useT();
  const [edit, setEdit] = useState(false);
  const [ko, setKo] = useState<string | null>(koValue(doc.ko_id, doc.ko_name));
  const [kc, setKc] = useState<string[]>(doc.parcel_numbers ?? []);
  const [evid, setEvid] = useState<string | null>(doc.evidencija ?? "KZ");
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true);
    try {
      const free = ko?.startsWith("free:");
      await http.patch(`/api/v1/dms/documents/${doc.id}`, { ko_id: ko && !free ? Number(ko) : null, ko_name: free ? ko!.slice(5) : "",
        parcels: ko ? kc : [], evidencija: ko ? evid : null });
      notifications.show({ color: "green", message: t("KO i čestice su sačuvani") });
      setEdit(false); onSaved();
    } catch (e) { notifications.show({ color: "red", message: (e as Error).message }); } finally { setBusy(false); }
  }
  return (
    <Card withBorder radius="md" p="sm">
      <Group justify="space-between" mb={edit ? "xs" : 0}>
        <div>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>{t("Katastar")}</Text>
          {!edit && (doc.ko_name ? <CadastralBadges doc={doc} />
            : <Text size="sm" c="dimmed">{t("Dokument nije vezan za katastarske čestice")}</Text>)}
        </div>
        {canEdit && !edit && <Button size="compact-xs" variant="subtle" onClick={() => setEdit(true)}>{t("Izmijeni")}</Button>}
      </Group>
      {edit && (
        <Stack gap="xs">
          <KoParcelsFields ko={ko} setKo={setKo} parcels={kc} setParcels={setKc} evid={evid} setEvid={setEvid} />
          <Group justify="flex-end" gap="xs">
            <Button size="xs" variant="default" onClick={() => setEdit(false)}>{t("Odustani")}</Button>
            <Button size="xs" loading={busy} onClick={save}>{t("Sačuvaj")}</Button>
          </Group>
        </Stack>
      )}
    </Card>
  );
}

/** Dokumenti (pretraga + zavođenje) — samostalno ili vezano za proizvođača / voćnjak / partiju. */
export function DocumentsPanel({ holdingId, entity }: { holdingId?: number; entity?: [string, number] }) {
  const { t } = useT();
  const can = useCan();
  const types = useDocTypes();
  const [q, setQ] = useState("");
  const [type, setType] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [up, setUp] = useState(false);
  const [sel, setSel] = useState<number | null>(null);
  const [fko, setFko] = useState<string | null>(null);
  const [fkc, setFkc] = useState("");
  const [fev, setFev] = useState<string | null>(null);
  const kos = useKoList();
  const docs = useQuery({ queryKey: ["dms", "list", q, type, page, holdingId, entity?.join(":"), fko, fkc, fev],
    queryFn: () => http.get<{ items: Doc[]; total: number }>("/api/v1/dms/documents", {
      q: q || undefined, doc_type: type, holding_id: holdingId, entity_type: entity?.[0], entity_id: entity?.[1],
      ko_id: fko, parcel: fkc || undefined, evidencija: fev, limit: 50, offset: (page - 1) * 50 }) });
  return (
    <Stack>
      <Group justify="space-between">
        <Group>
          <TextInput leftSection={<IconSearch size={14} />} placeholder={t("Pretraga punog teksta, naslova, broja...")} value={q}
            onChange={(e) => { setQ(e.currentTarget.value); setPage(1); }} w={320} />
          <Select placeholder={t("Tip dokumenta")} clearable data={(types.data ?? []).map((x) => ({ value: x.code, label: x.name }))} value={type} onChange={setType} w={240} />
          <Select placeholder={t("Evidencija")} clearable value={fev} onChange={(v) => { setFev(v); setPage(1); }} w={200}
            data={EVIDENCIJE.map((e) => ({ value: e.value, label: `${e.value} — ${t(e.title)}` }))} />
          <Select placeholder={t("KO")} clearable searchable limit={60} value={fko} onChange={(v) => { setFko(v); setPage(1); }} w={190}
            data={(kos.data ?? []).map((k) => ({ value: String(k.id), label: k.name }))} />
          <TextInput placeholder={t("KČ (npr. 1234/1)")} value={fkc} onChange={(e) => { setFkc(e.currentTarget.value.trim()); setPage(1); }} w={150} disabled={!fko} />
        </Group>
        {can("dms:write") && <Button leftSection={<IconUpload size={16} />} onClick={() => setUp(true)}>{t("Zavedi dokument")}</Button>}
      </Group>
      <DataTable rows={(docs.data?.items ?? []) as unknown as Record<string, unknown>[]} search={false} onRowClick={(r) => setSel(Number(r.id))} columns={[
        { key: "reg_no", label: "Broj", render: (r) => <Text size="sm" ff="monospace">{String(r.reg_no)}</Text> },
        { key: "title", label: "Naslov", render: (r) => <><Text size="sm">{String(r.title)}</Text>
          {r.snippet ? <Text size="xs" c="dimmed" dangerouslySetInnerHTML={{ __html: String(r.snippet) }} /> : null}</> },
        { key: "doc_type_name", label: "Tip" }, { key: "doc_date", label: "Datum", type: "date" },
        { key: "producer", label: "Proizvođač" }, { key: "opstina", label: "Opština" },
        { key: "ko_name", label: "KO / KČ", render: (r) => r.ko_name ? <Text size="xs">{r.evidencija ? <b>{String(r.evidencija)} · </b> : null}{String(r.ko_name)}<br />
          <Text span c="dimmed" size="xs">{((r.parcel_numbers as string[]) ?? []).map((n, i) => <span key={n} style={{ color: ((r.parcel_unknown as string[]) ?? []).includes(n) ? "var(--mantine-color-orange-6)" : undefined }}>{i ? ", " : ""}{n}</span>)}</Text></Text> : "—" },
        { key: "current_version", label: "Verzija", type: "integer" }, { key: "retain_until", label: "Čuva se do", type: "date" },
        { key: "status", label: "Status", render: (r) => <StatusBadge status={String(r.status)} /> },
      ]} />
      <Pagination total={Math.max(1, Math.ceil((docs.data?.total ?? 0) / 50))} value={page} onChange={setPage} size="sm" />
      <Modal opened={up} onClose={() => setUp(false)} title={t("Zavođenje dokumenta")} size="lg">
        <UploadForm holdingId={holdingId} entity={entity} onDone={() => setUp(false)} />
      </Modal>
      {sel && <DocDetail id={sel} onClose={() => setSel(null)} />}
    </Stack>
  );
}

export default function DmsPage() {
  const { t } = useT();
  const can = useCan();
  const stats = useQuery({ queryKey: ["dms", "stats"], queryFn: () => http.get<{ documents: number; versions: number; bytes: number; expiring_90d: number;
    by_type: { name: string; n: number }[] }>("/api/v1/dms/stats") });
  const book = useQuery({ queryKey: ["dms", "book"], enabled: can("dms:admin"), queryFn: () => http.get<Record<string, unknown>[]>("/api/v1/dms/archive-book") });
  const review = useQuery({ queryKey: ["dms", "review"], enabled: can("dms:admin"), queryFn: () => http.get<Record<string, unknown>[]>("/api/v1/dms/retention-review") });
  const s = stats.data;
  return (
    <Stack>
      <PageHeader title="Upravljanje dokumentima" description="Zavođenje, veza na katastarsku opštinu i jednu ili više katastarskih čestica sa djelovodnim brojem, verzije, pretraga punog teksta, rokovi čuvanja i arhivska knjiga (Uredba o kancelarijskom poslovanju RS; Zakon o arhivskoj djelatnosti RS)." />
      {s && <KpiGrid items={[{ label: "Dokumenata", value: fmtNum(s.documents, 0) }, { label: "Verzija", value: fmtNum(s.versions, 0) },
        { label: "Zauzeće", value: fmtSize(s.bytes) }, { label: "Rok čuvanja ističe (90 dana)", value: s.expiring_90d, color: "orange" }]} />}
      <Tabs defaultValue="docs">
        <Tabs.List>
          <Tabs.Tab value="docs">{t("Dokumenti")}</Tabs.Tab>
          {can("dms:admin") && <Tabs.Tab value="book">{t("Arhivska knjiga")}</Tabs.Tab>}
          {can("dms:admin") && <Tabs.Tab value="review">{t("Odabiranje (istekao rok)")}</Tabs.Tab>}
          <Tabs.Tab value="types">{t("Po tipovima")}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="docs" pt="md"><DocumentsPanel /></Tabs.Panel>
        <Tabs.Panel value="book" pt="md">
          <DataTable rows={book.data ?? []} exportName="arhivska_knjiga" columns={[
            { key: "archive_book_no", label: "Redni broj", type: "integer" }, { key: "reg_no", label: "Djelovodni broj" },
            { key: "title", label: "Sadržaj" }, { key: "tip", label: "Tip" }, { key: "classification", label: "Klas. oznaka" },
            { key: "retention_years", label: "Rok (god.)", type: "integer" }, { key: "archival", label: "Arhivska građa", type: "bool" },
            { key: "archived_at", label: "Arhivirano", type: "date" }, { key: "retain_until", label: "Čuva se do", type: "date" },
          ]} />
        </Tabs.Panel>
        <Tabs.Panel value="review" pt="md">
          <Text size="sm" c="dimmed" mb="sm">{t("Dokumenti kojima je istekao rok čuvanja. Izlučivanje se sprovodi komisijski uz saglasnost nadležnog arhiva — sistem ništa ne briše automatski.")}</Text>
          <DataTable rows={review.data ?? []} exportName="odabiranje" columns={[
            { key: "reg_no", label: "Broj" }, { key: "title", label: "Naslov" }, { key: "tip", label: "Tip" },
            { key: "archival", label: "Arhivska građa", type: "bool" }, { key: "retain_until", label: "Rok istekao", type: "date" },
            { key: "status", label: "Status" },
          ]} />
        </Tabs.Panel>
        <Tabs.Panel value="types" pt="md">
          <Card withBorder>
            {(s?.by_type ?? []).length ? (s?.by_type ?? []).map((x) => (
              <Group key={x.name} justify="space-between" py={4}><Text size="sm">{x.name}</Text><Badge variant="light">{x.n}</Badge></Group>
            )) : <Empty />}
            <Anchor size="sm" href="/admin/dms" mt="sm">{t("Uređivanje tipova dokumenata i rokova čuvanja")}</Anchor>
          </Card>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
